import React, { useState, useEffect, useRef } from 'react';
import { Gradient } from 'stripe-gradient';
import { easings, useReducedMotion, useSpringValue } from '@react-spring/web';

import { useContentfulInspectorMode } from '@contentful/live-preview/react';

import { Container, Row, Section } from 'src/components-v2/Layout';
import useIntersection from 'src/hooks/useIntersection/useIntersection';
import useIsPotato from 'src/hooks/useIsPotato';
import { useTrack } from 'src/contexts/AnalyticsContext';
import { Events } from 'src/lib/trackingEvents';
import { isAutomatedBrowser } from 'src/lib/browser-utils';
import { ACHCard, GRADIENT_ACH_CANVAS_ID } from './ACHCard';
import { ACHForm } from './ACHForm';
import type { IUserInput, ICalculation } from './ACHCalculator.types';
import { calculate } from './ACHCalculator.utils';
import { resizeUnfilledSlider } from '../Calculator/Calculator.utils';
import Column from 'src/components-v2/Layout/Grid/Column';
import Stack from 'src/components-v2/Layout/Stack/Stack';

const CalcDefaults = {
  monthlyVolume: 175000,
  averagePaymentSize: 153,
  achAdoption: 25,
};

export const ACHCalculator = ({ fields, sys }) => {
  const track = useTrack();
  const inspectorProps = useContentfulInspectorMode({
    entryId: sys?.id,
  });
  const isPotato = useIsPotato();
  const prefersReducedMotion = useReducedMotion();
  const sectionRef = useRef<HTMLElement>(null);
  const isInView = useIntersection({
    element: sectionRef,
    threshold: 0.01,
    useOnce: true,
  });

  // Render the settled defaults on first paint instead of the onload slide-in,
  // so Happo can't snapshot the calculator mid-animation (flaky 0 → values).
  const settleImmediately =
    isPotato || prefersReducedMotion || isAutomatedBrowser();

  // Check if formBody contains an h1 node
  const hasH1 = fields?.formBody?.content?.some((node) => {
    return node.nodeType === 'heading-1';
  });

  // app state
  const [userInput, setUserInput] = useState<IUserInput>(
    settleImmediately
      ? { ...CalcDefaults, months: 12 }
      : { achAdoption: 0, averagePaymentSize: 0, monthlyVolume: 0, months: 12 },
  );
  const [calcState, setCalcState] = useState<ICalculation>(
    calculate(userInput),
  );

  // refs
  const achAdoptionInputRef = useRef<HTMLInputElement>(null);
  const achAdoptionSliderRef = useRef<HTMLInputElement>(null);
  const averagePaymentSizeInputRef = useRef<HTMLInputElement>(null);
  const averagePaymentSizeSliderRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const hasUserInteracted = useRef(false);
  const isDragging = useRef(false);
  const monthlyVolumeInputRef = useRef<HTMLInputElement>(null);
  const monthlyVolumeSliderRef = useRef<HTMLInputElement>(null);
  const dragGhostImageRef = useRef<HTMLImageElement | null>(null);

  // animation values
  const translateCanvas = useSpringValue('translateX(0%) rotate(39deg)', {
    config: {
      duration: 600,
      easing: easings.easeInOutBack,
    },
  });
  const translateDraggable = useSpringValue('translateX(0%)', {
    config: {
      duration: 600,
      easing: easings.easeInOutBack,
    },
  });
  const translateWrapper = useSpringValue('translateX(0%)', {
    config: {
      duration: 600,
      easing: easings.easeInOutBack,
    },
  });
  const monthlyVolumeSliderValue = useSpringValue(
    settleImmediately ? CalcDefaults.monthlyVolume : 0,
    {
      config: {
        duration: 1000,
        easing: easings.easeInOutBack,
      },
    },
  );
  const averagePaymentSizeSliderValue = useSpringValue(
    settleImmediately ? CalcDefaults.averagePaymentSize : 0,
    {
      config: {
        duration: 1000,
        easing: easings.easeInOutBack,
      },
    },
  );
  const achAdoptionSliderValue = useSpringValue(
    settleImmediately ? CalcDefaults.achAdoption : 0,
    {
      config: {
        duration: 1000,
        easing: easings.easeInOutBack,
      },
    },
  );

  // updates the the width of the unfilled part of the slider
  useEffect(() => {
    if (!achAdoptionSliderRef?.current) return;
    if (!averagePaymentSizeSliderRef?.current) return;
    if (!monthlyVolumeSliderRef?.current) return;
    resizeUnfilledSlider({ ref: achAdoptionSliderRef });
    resizeUnfilledSlider({ ref: averagePaymentSizeSliderRef });
    resizeUnfilledSlider({ ref: monthlyVolumeSliderRef });
  }, [
    userInput.achAdoption,
    userInput.averagePaymentSize,
    userInput.monthlyVolume,
  ]);

  // recalculate on change
  useEffect(() => {
    setCalcState(calculate(userInput));
  }, [userInput]);

  // gradient button animation (unique id so mortgage + ACH on same page don't conflict)
  useEffect(() => {
    const gradient = new Gradient();
    // Freeze to one deterministic frame for reduced-motion users, low-perf
    // devices, or automated capture (see `isAutomatedBrowser`). `isStatic`
    // renders a single seeded frame, then disconnects.
    if (isPotato || prefersReducedMotion || isAutomatedBrowser()) {
      (gradient as { isStatic?: boolean }).isStatic = true;
    }
    gradient.initGradient(`#${GRADIENT_ACH_CANVAS_ID}`);
    return () => {
      (gradient as { disconnect?: () => void }).disconnect?.();
    };
  }, [isPotato, prefersReducedMotion]);

  // Create transparent drag image on mount
  useEffect(() => {
    const img = new Image();
    img.src =
      'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
    dragGhostImageRef.current = img;
  }, []);

  const hasAnimated = useRef(false);

  // Run onload animation and track VIEWED only when component is in view
  useEffect(() => {
    if (!isInView) return;

    // Already settled (see `settleImmediately`) — skip the slide-in, just track the view.
    if (settleImmediately) {
      track({ type: Events.VIEWED, payload: { ...fields?.trackingContext } });
      return;
    }

    // Reset so each mount (e.g. after React Strict Mode remount) can run the animation
    hasAnimated.current = false;

    let cancelled = false;
    let timeoutId: number | null = null;

    const tryStartAnimations = () => {
      if (cancelled) return;
      if (
        !monthlyVolumeSliderRef.current ||
        !averagePaymentSizeSliderRef.current ||
        !achAdoptionSliderRef.current
      ) {
        return false;
      }

      hasAnimated.current = true;

      monthlyVolumeSliderValue.start({
        to: CalcDefaults.monthlyVolume,
        onChange: () => {
          resizeUnfilledSlider({ ref: monthlyVolumeSliderRef });
        },
        onRest: () => {
          setUserInput((prev) => {
            return {
              ...prev,
              monthlyVolume: CalcDefaults.monthlyVolume,
            };
          });
        },
      });

      averagePaymentSizeSliderValue.start({
        to: CalcDefaults.averagePaymentSize,
        onChange: () => {
          resizeUnfilledSlider({ ref: averagePaymentSizeSliderRef });
        },
        onRest: () => {
          setUserInput((prev) => {
            return {
              ...prev,
              averagePaymentSize: CalcDefaults.averagePaymentSize,
            };
          });
        },
      });

      achAdoptionSliderValue.start({
        to: CalcDefaults.achAdoption,
        onChange: () => {
          resizeUnfilledSlider({ ref: achAdoptionSliderRef });
        },
        onRest: () => {
          setUserInput((prev) => {
            return {
              ...prev,
              achAdoption: CalcDefaults.achAdoption,
            };
          });
        },
      });

      track({
        type: Events.VIEWED,
        payload: {
          ...fields?.trackingContext,
        },
      });
      return true;
    };

    // Wait for layout (two frames) then a short delay so refs are attached; retry if refs not ready
    const scheduleTry = (attempt = 0) => {
      if (cancelled) return;
      const maxAttempts = 20;
      const delay = attempt === 0 ? 150 : 50;

      timeoutId = window.setTimeout(() => {
        if (cancelled) return;
        if (tryStartAnimations()) return;
        if (attempt < maxAttempts - 1) {
          scheduleTry(attempt + 1);
        }
      }, delay);
    };

    const frameId = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        scheduleTry(0);
      });
    });

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frameId);
      if (timeoutId != null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [
    isInView,
    settleImmediately,
    track,
    fields?.trackingContext,
    monthlyVolumeSliderValue,
    averagePaymentSizeSliderValue,
    achAdoptionSliderValue,
  ]);

  /**
   * Event handlers
   */
  const handleChange = ({ target: { name, value } }) => {
    const sanitizedValue = value.replace(/[^0-9]/g, '');
    setUserInput((prevState) => {
      return {
        ...prevState,
        [name]: sanitizedValue,
      };
    });
    setHasUserInteracted();
  };

  const handleDragEnd = () => {
    setTimeout(() => {
      isDragging.current = false;
    }, 100);
  };

  const handleDragOver = () => {
    if (isDragging.current) return;
    if (userInput.months === 12) {
      translateDraggable.start(`translateX(${50}%)`);
      togglePeriod({ target: { value: '1' } });
    } else {
      translateDraggable.start(`translateX(${0}%)`);
      togglePeriod({ target: { value: '12' } });
    }
  };

  const handleDragStart = (e: React.DragEvent) => {
    // Hide the default drag ghost image
    if (dragGhostImageRef.current) {
      e.dataTransfer.setDragImage(dragGhostImageRef.current, 0, 0);
    }

    setTimeout(() => {
      isDragging.current = true;
    }, 100);
  };

  const handleToggle = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      togglePeriod({ target: { value: userInput.months === 12 ? '1' : '12' } });
    }
  };

  const setHasUserInteracted = () => {
    if (!hasUserInteracted?.current) {
      hasUserInteracted.current = true;
      track({
        type: Events.CHANGED,
        payload: {
          ...fields?.trackingContext,
        },
      });
    }
  };

  const togglePeriod = ({ target: { value } }) => {
    const isToggled = value === '1';
    translateWrapper.start(`translateX(${isToggled ? 100 : 0}%)`, {
      onRest: () => {
        setUserInput((prevState) => {
          return {
            ...prevState,
            months: Number(value),
          };
        });
      },
    });
    translateCanvas.start(
      `translateX(${isToggled ? -30 : 0}%) rotate(39deg)`,
      {},
    );
    translateDraggable.start(`translateX(${isToggled ? 100 : 0}%)`);
    setHasUserInteracted();
  };

  return (
    <Section
      ref={sectionRef}
      component={hasH1 ? 'header' : 'section'}
      {...inspectorProps({ fieldId: 'internalTitle' })}
      sx={fields?.styles}
    >
      <Container>
        <Row>
          <Column
            xsOffset={1}
            xs={22}
            smOffset={0}
            sm={24}
            lgOffset={2}
            lg={20}
          >
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              gap={{ xs: 5.25, sm: 9.125, lg: 17.5 }}
            >
              <ACHForm
                achAdoptionInputRef={achAdoptionInputRef}
                achAdoptionSliderRef={achAdoptionSliderRef}
                averagePaymentSizeInputRef={averagePaymentSizeInputRef}
                averagePaymentSizeSliderRef={averagePaymentSizeSliderRef}
                fields={fields}
                formRef={formRef}
                handleChange={handleChange}
                hasUserInteracted={hasUserInteracted}
                monthlyVolumeInputRef={monthlyVolumeInputRef}
                monthlyVolumeSliderRef={monthlyVolumeSliderRef}
                sys={sys}
                userInput={userInput}
                monthlyVolumeSliderValue={monthlyVolumeSliderValue}
                averagePaymentSizeSliderValue={averagePaymentSizeSliderValue}
                achAdoptionSliderValue={achAdoptionSliderValue}
              />
              <ACHCard
                calcState={calcState}
                fields={fields}
                handleDragEnd={handleDragEnd}
                handleDragOver={handleDragOver}
                handleDragStart={handleDragStart}
                handleToggle={handleToggle}
                isPotato={isPotato}
                sys={sys}
                togglePeriod={togglePeriod}
                translateCanvas={translateCanvas}
                translateDraggable={translateDraggable}
                translateWrapper={translateWrapper}
                userInput={userInput}
              />
            </Stack>
          </Column>
        </Row>
      </Container>
    </Section>
  );
};
